import {
  Clock, Hash, MoreHorizontal, Plus, Route, Search, Trash2, X,
} from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { CopyButton, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { RouteDidDialog } from "@/features/did-route-form";
import { RequestNumberDialog } from "@/features/request-number";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCancelOrder, useDids, useOrders, useReleaseDid } from "@/lib/queries";
import { formatRequirements } from "@/lib/skills";
import {
  ORDER_STATUS_LABELS, ROUTE_LABELS, ROUTE_TYPES, atLeast, routeIsBroken, routeSummary,
  type Did, type NumberOrder,
} from "@/lib/types";
import { formatDateTime, relativeTime } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";

/**
 * The numbers this organisation holds, and where a call to each one goes.
 *
 * Two lists. The requests card shows what is still with the superadmin (and what came back
 * refused), because "I asked for a number last week" needs an answer on the same screen. The
 * numbers table is the routing: every row is a number the tenant may point at a campaign, a
 * queue or a rep. A number that arrived unrouted is called out — the server creates it that
 * way on purpose, and it takes no calls until someone routes it.
 */
export function NumbersPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";

  const [search, setSearch] = React.useState("");
  const [routeType, setRouteType] = React.useState("all");
  const debounced = useDebounced(search, 250);

  const { data, isPending, error, refetch } = useDids({
    q: debounced || undefined,
    action_type: routeType === "all" ? undefined : routeType,
  });
  const { data: orders } = useOrders({}, { enabled: canManage });

  const [requesting, setRequesting] = React.useState(false);
  const [routing, setRouting] = React.useState<Did | undefined>();
  const [releasing, setReleasing] = React.useState<Did | undefined>();
  const [cancelling, setCancelling] = React.useState<NumberOrder | undefined>();
  const release = useReleaseDid();
  const cancel = useCancelOrder();

  const filtered = Boolean(debounced) || routeType !== "all";
  const unrouted = (data ?? []).filter((d) => d.action_type === null).length;
  // Decided-and-approved requests have become numbers in the table; the rest still need eyes.
  const openOrders = (orders ?? []).filter((o) => o.status !== "approved");

  return (
    <Page>
      <PageHeader
        title="Numbers"
        description="The numbers this organisation holds, and where a call to each one goes."
        action={
          canManage && (
            <Button variant="default" onClick={() => setRequesting(true)}>
              <Plus />
              Request a number
            </Button>
          )
        }
      />

      {openOrders.length > 0 && (
        <Card className="mb-4 overflow-hidden">
          <CardHeader
            title="Requests"
            description="Numbers asked for from the pool. A superadmin approves each one; it then appears below, ready to route."
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {openOrders.map((o) => (
                <TableRow key={o.id}>
                  <MonoCell>{o.number}</MonoCell>
                  <TableCell className="text-text-muted">{o.note ?? "—"}</TableCell>
                  <TableCell>
                    <OrderStatus order={o} />
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    <Tooltip content={formatDateTime(o.requested_at)}>
                      <span>
                        {relativeTime(o.requested_at)}
                        {o.requested_by_email ? ` · ${o.requested_by_email}` : ""}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {o.status === "pending_approval" && (
                      <Tooltip content="Withdraw the request">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Cancel the request for ${o.number}`}
                          onClick={() => setCancelling(o)}
                        >
                          <X />
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {unrouted > 0 && canManage && !filtered && (
        <Callout tone="warning" className="mb-4">
          {unrouted === 1 ? "One number is" : `${unrouted} numbers are`} not routed yet. Calls to
          an unrouted number hear that it cannot be reached — set a route to bring it into
          service.
        </Callout>
      )}

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number or description"
            leading={<Search />}
            className="w-64"
            aria-label="Search numbers"
          />
          <Select value={routeType} onValueChange={setRouteType}>
            <SelectTrigger className="w-40" aria-label="Filter by route">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any route</SelectItem>
              {ROUTE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {ROUTE_LABELS[t]}
                </SelectItem>
              ))}
              <SelectItem value="none">Not routed</SelectItem>
            </SelectContent>
          </Select>
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setRouteType("all");
              }}
            >
              Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-text-muted">
            {data ? `${data.length} number${data.length === 1 ? "" : "s"}` : null}
          </span>
        </Toolbar>

        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load numbers"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Hash}
            title={filtered ? "No numbers match those filters" : "No numbers yet"}
            description={
              filtered
                ? "Try clearing the search or the route filter."
                : canManage
                  ? "Request a number from the pool. Once a superadmin approves it, route it here."
                  : "Nothing has been assigned to this organisation yet."
            }
            action={
              canManage && !filtered ? (
                <Button variant="default" onClick={() => setRequesting(true)}>
                  <Plus />
                  Request a number
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Route</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => {
                const broken = routeIsBroken(d);
                return (
                  <TableRow key={d.id}>
                    <MonoCell>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-text">{d.number}</span>
                        <CopyButton value={d.number} />
                      </span>
                    </MonoCell>
                    <TableCell className="text-text-muted">{d.description ?? "—"}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        {d.action_type ? (
                          <Badge tone={broken ? "danger" : "accent"} size="sm">
                            {ROUTE_LABELS[d.action_type]}
                          </Badge>
                        ) : (
                          <Badge tone="warning" size="sm">
                            Not routed
                          </Badge>
                        )}
                        <span className={broken ? "text-danger-11" : "text-text"}>
                          {d.action_type ? routeSummary(d) : ""}
                        </span>
                      </span>
                      {d.action_type && d.action_type !== "campaign" && d.campaign_name && (
                        <span className="ml-2 text-xs text-text-subtle">for {d.campaign_name}</span>
                      )}
                      {d.action_type === "campaign" && d.rep_on_campaign === false && (
                        <>
                          <Badge tone="warning" size="sm" className="ml-2">
                            Not on the campaign
                          </Badge>
                          <span className="block text-xs text-text-muted">
                            Calls go to anyone on the line until the rep staffs the campaign again.
                          </span>
                        </>
                      )}
                      {d.routing_tags.length > 0 && (
                        <Badge tone="neutral" size="sm" className="ml-2">
                          Needs {formatRequirements(d.routing_tags)}
                        </Badge>
                      )}
                    </TableCell>
                    {isSuperadmin && (
                      <TableCell className="text-xs text-text-muted">{d.tenant_name ?? "—"}</TableCell>
                    )}
                    <TableCell>
                      {d.enabled ? (
                        <Badge tone="success" size="sm">
                          In service
                        </Badge>
                      ) : (
                        <Badge tone="outline" size="sm">
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${d.number}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => setRouting(d)}>
                              <Route />
                              {d.action_type ? "Change route" : "Set route"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem destructive onSelect={() => setReleasing(d)}>
                              <Trash2 />
                              Release number
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <RequestNumberDialog open={requesting} onOpenChange={setRequesting} />
      <RouteDidDialog
        did={routing}
        open={Boolean(routing)}
        onOpenChange={(open) => !open && setRouting(undefined)}
      />

      <ConfirmDialog
        open={Boolean(releasing)}
        onOpenChange={(open) => !open && setReleasing(undefined)}
        title="Release this number?"
        description={
          <>
            <span className="font-mono">{releasing?.number}</span> goes back to the pool and stops
            taking calls. Its route is discarded. Another organisation may claim it afterwards.
          </>
        }
        confirmLabel="Release number"
        destructive
        loading={release.isPending}
        onConfirm={async () => {
          if (!releasing) return;
          try {
            await release.mutateAsync(releasing.id);
            toast.success("Number released", releasing.number);
            setReleasing(undefined);
          } catch (e) {
            toast.error("Could not release", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(cancelling)}
        onOpenChange={(open) => !open && setCancelling(undefined)}
        title="Withdraw this request?"
        description={
          <>
            <span className="font-mono">{cancelling?.number}</span> returns to the pool for anyone
            to request.
          </>
        }
        confirmLabel="Withdraw request"
        loading={cancel.isPending}
        onConfirm={async () => {
          if (!cancelling) return;
          try {
            await cancel.mutateAsync(cancelling.id);
            toast.success("Request withdrawn", cancelling.number);
            setCancelling(undefined);
          } catch (e) {
            toast.error("Could not withdraw", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}

/** A request's state, with the verdict when there is one. */
export function OrderStatus({ order }: { order: NumberOrder }) {
  switch (order.status) {
    case "pending_approval":
      return (
        <Tooltip
          content={
            order.expires_at
              ? `Lapses ${relativeTime(order.expires_at)} if nobody decides`
              : "Held until somebody decides"
          }
        >
          <span className="inline-flex">
            <Badge tone="warning" size="sm">
              <Clock className="size-3" aria-hidden />
              {ORDER_STATUS_LABELS[order.status]}
            </Badge>
          </span>
        </Tooltip>
      );
    case "approved":
      return <Badge tone="success" size="sm">{ORDER_STATUS_LABELS[order.status]}</Badge>;
    case "rejected":
      return (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="danger" size="sm">{ORDER_STATUS_LABELS[order.status]}</Badge>
          {order.reject_reason && (
            <span className="text-xs text-text-muted">{order.reject_reason}</span>
          )}
        </span>
      );
    default:
      return <Badge tone="outline" size="sm">{ORDER_STATUS_LABELS[order.status]}</Badge>;
  }
}
